import {useTranslation} from "react-i18next";
import {isNil} from "app/utils/stdlib";
import {IonButton, IonIcon, IonLabel, useIonActionSheet, useIonLoading, useIonRouter} from "@ionic/react";
import {
  arrowBackOutline,
  closeOutline,
  ellipsisVerticalCircleOutline,
  printOutline,
  shareSocialOutline
} from "ionicons/icons";
import {useCallback} from "react";
import {linkEmplControlCardInstanceBack} from "app/employee/core/employeeLinks";
import {type PrintControlCardInstances, PrintType, savePrintState} from "app/state/print";
import {linkPdfExport} from "app/components/hooks/useLinks";
import {getControlCardPrintTitle} from "app/components/admin/employee/utils";
import {useShareAction} from "app/employee/hooks/useShareAction";
import type {ControlCardInstanceFullFragment} from "app/gql/graphqlSchema";
import {usePermissions} from "app/employee/hooks/usePermissions";

interface ContextMenuProps {
  onShare: () => void | Promise<void>;
  onPrint: () => void | Promise<void>;
  onBack: () => void | Promise<void>;
  completed: boolean;
  canEdit: boolean;
}
const useContextMenu = ({onPrint, onShare, onBack}: ContextMenuProps) => {
  const {t} = useTranslation();
  const [presentActionSheet] = useIonActionSheet();

  const showOptions = useCallback(async () => {
    return presentActionSheet({
      header: t("control-card.action.title"),
      buttons: [
        {
          text: t("control-card.action.print"),
          icon: printOutline,
          handler: onPrint
        },
        {
          text: t("g.share.button"),
          icon: shareSocialOutline,
          handler: onShare
        },
        {
          text: t("g.back"),
          icon: arrowBackOutline,
          handler: onBack
        },
        {
          text: t("g.close"),
          icon: closeOutline,
          role: "cancel"
        }
      ]
    });
  }, [onBack, onPrint, onShare, presentActionSheet, t]);

  return {showOptions};
};

interface Props {
  instance: ControlCardInstanceFullFragment;
}

export const InstanceViewActionButton = ({instance}: Props) => {
  const {t} = useTranslation();
  const {push} = useIonRouter();
  const {canEditControlCardLocation} = usePermissions();
  const canEdit = canEditControlCardLocation(instance.location.id, instance.date);
  const shareAttach = useShareAction();
  const [presentLoading, dismissLoading] = useIonLoading();

  const backHandler = useCallback(() => {
    push(linkEmplControlCardInstanceBack(instance), "back");
  }, [instance, push]);

  const createPdfLink = useCallback(async () => {
    const printState = {
      type: PrintType.CONTROL_CARD_INSTANCES,
      instance
    } satisfies PrintControlCardInstances;
    const humanName = getControlCardPrintTitle(instance);
    return linkPdfExport(await savePrintState(printState, humanName));
  }, [instance]);

  const print = useCallback(async () => {
    window.open(await createPdfLink(), "_blank", "noopener");
  }, [createPdfLink]);

  const completed = !isNil(instance.completedBy);
  const {showOptions} = useContextMenu({
    completed,
    onShare: async () => {
      let url: string;
      try {
        await presentLoading({
          message: t("g.share.download.downloading")
        });
        url = await createPdfLink();
      } finally {
        await dismissLoading();
      }

      const humanName = getControlCardPrintTitle(instance);
      await shareAttach({
        url,
        humanName,
        filename: `${humanName}.pdf`
      });
    },
    onPrint: print,
    onBack: backHandler,
    canEdit
  });

  return (
    <IonButton onClick={showOptions} shape="round">
      <IonIcon icon={ellipsisVerticalCircleOutline} slot="end" />
      <IonLabel>{t("g.action", {defaultValue: "Действия"})}</IonLabel>
    </IonButton>
  );
};
